import React, { useEffect, useContext, useState } from 'react';
import styled from 'styled-components';
import { InView } from 'react-intersection-observer';
import { uniq, sortBy } from 'lodash';

import { Button, Throbber as ThrobberComponent } from '@lt/components';
import { useTranslation } from '@lt/localization';

import { isSSR } from 'src/helpers/ssr';
import { pushExploreHotelClicked } from 'src/routes/SeoApp/utils/gtmEvents';

// Components:
import {
  HotelListBanner,
  HOTEL_LIST_BANNER_EXPLORE,
} from 'src/components/HotelListBanner';
import HotelsLoadingPreview from 'src/components/Hotel/HotelsLoadingPreview';
import { HotelCard } from './components/HotelCard';
import EmptyHotels from './components/EmptyHotels';

// Context:
import { MobileContext } from '../../domain/context/MobileContext';

import type { IHotelsContainer, THotelCoordinate } from './types';

export const HotelsContainer = (props: IHotelsContainer): JSX.Element => {
  const {
    className,
    hotels,
    buildUrl,
    hotelsCount,
    mapExpanded,
    fallbackSearch,
    showMore,
    setCurrentVisibleHotels,
    hotelHover,
    awaitFiltration,
    awaitPagination,
    modifierStyles,
    isWhiteLabel,
    isWebview,
    isHotelOnly,
    currencyCode,
    isWishlistAvailable,
    activateCashbackForPartner,
    customCashbackIconURL,
    customCashbackText,
    customCashbackTooltipText,
    isLiveSearchAllowed,
    getImages,
  } = props;

  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();

  // стейт массива индексов отелей, находящихся в зоне видимости viewport
  const [visibleHotels, setVisibleHotels] = useState<number[]>([]);

  useEffect(() => {
    if (isMobile) {
      if (!isSSR) window.addEventListener('popstate', buildUrl);
    }
    return () => {
      if (isMobile) {
        if (!isSSR) window.removeEventListener('popstate', buildUrl);
      }
    };
  }, [buildUrl, isMobile]);

  useEffect(() => {
    if (awaitFiltration) {
      setCurrentVisibleHotels(visibleHotels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitFiltration]);

  const [hotelCoordinates, setHotelCoordinates] = useState<THotelCoordinate>();
  useEffect(() => {
    let timeout;
    if (hotelCoordinates) {
      timeout = setTimeout(() => {
        hotelHover(hotelCoordinates);
      }, 600);
    }

    return () => (timeout ? clearTimeout(timeout) : undefined);
  }, [hotelCoordinates, hotelHover]);

  if ((!Array.isArray(hotels) || !hotels.length) && !awaitFiltration) {
    return <EmptyHotels isMobile={isMobile} />;
  }

  const handleClickShowMore = () => {
    showMore();
  };
  const displayShowMoreBtn = hotelsCount > hotels.length && !fallbackSearch;

  const handleBlur = () => setHotelCoordinates(undefined);
  const handleHover = (hotelId, index) => {
    const hoveredHotel = hotels.find((h) => h.hotel.id === hotelId);
    const {
      hotel: { id, long, lat },
    } = hoveredHotel || {};

    if (long && lat) {
      const activeHotelCoordinates: THotelCoordinate = {
        index,
        id,
        coordinates: [long, lat],
      };

      setHotelCoordinates(activeHotelCoordinates);
    }
  };

  const handleHotelVisibility = (index: number, inView: boolean) => {
    let newVisibleHotels = [...visibleHotels];

    if (inView) newVisibleHotels.push(index);
    else newVisibleHotels = newVisibleHotels.filter((n) => n !== index);

    // сортируем по возрастанию и убираем дубли
    setVisibleHotels(uniq(sortBy([...newVisibleHotels])));
  };

  const handleImagesLoad = (hotelId) => {
    if (!isLiveSearchAllowed) return;

    getImages(hotelId);
  };

  return (
    <>
      <StyledHotelList
        $mapExpanded={mapExpanded}
        $isMobile={isMobile}
        className={className}
      >
        {awaitFiltration ? (
          <HotelsLoadingPreview
            visibleLimit={hotels.length}
            smallCard={isMobile}
          />
        ) : (
          hotels.map((hotel, index) => (
            <>
              <StyledBanner
                index={index}
                type={HOTEL_LIST_BANNER_EXPLORE}
                isWhitelabel={isWhiteLabel}
                isMobile={isMobile}
              />
              <HotelCardWrapper
                key={hotel.hotel.id}
                onClick={
                  isMobile
                    ? () => handleHover(hotel.hotel.id, index)
                    : () => undefined
                }
                onMouseEnter={() => handleHover(hotel.hotel.id, index)}
                onMouseLeave={handleBlur}
              >
                <InView
                  onChange={(inView) => handleHotelVisibility(index, inView)}
                >
                  <HotelCard
                    index={index}
                    isPreloadable={index <= 1}
                    key={hotel.hotelHref}
                    {...hotel}
                    isMobile={isMobile}
                    hasLabel={Boolean(
                      hotel.labels?.find(
                        (label) => label.context?.search === 1,
                      ),
                    )}
                    isSmallCard={mapExpanded || isMobile}
                    isWhiteLabel={isWhiteLabel}
                    openInCurrentTab={isWebview}
                    activateCashbackForPartner={activateCashbackForPartner}
                    customCashbackIconURL={customCashbackIconURL}
                    customCashbackText={customCashbackText}
                    customCashbackTooltipText={customCashbackTooltipText}
                    isHotelOnly={isHotelOnly}
                    hotelInfo={{
                      min_price: hotel.min_price,
                      hotel: { id: hotel.hotel.id },
                    }}
                    currencyCode={currencyCode}
                    dataLayer={{
                      hotelClicked: () =>
                        pushExploreHotelClicked({
                          hotel_id: hotel.hotel.id,
                        }),
                    }}
                    isFlexDates /* on Explore always show extended card with nights */
                    isWishlistAvailable={isWishlistAvailable}
                    loadAllImages={() => handleImagesLoad(hotel.hotel.id)}
                  />
                </InView>
              </HotelCardWrapper>
            </>
          ))
        )}
      </StyledHotelList>

      {displayShowMoreBtn ? (
        <StyledButton
          type="button"
          onClick={handleClickShowMore}
          isMobile={isMobile}
          textColor={modifierStyles.textColor}
        >
          {awaitPagination ? (
            <ThrobberComponent size={18} color="#007aff" />
          ) : (
            t('explore.hotels.showMore')
          )}
        </StyledButton>
      ) : null}
    </>
  );
};

const HotelCardWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const StyledHotelList = styled.div<{
  $mapExpanded: boolean;
  $isMobile: boolean;
}>`
  width: ${({ $mapExpanded, $isMobile }) =>
    $mapExpanded && !$isMobile ? '320px' : 'inherit'};
`;

const StyledButton = styled(Button)<{
  isMobile: boolean;
  textColor: string | null;
}>`
  position: relative;
  margin: auto;
  width: ${({ isMobile }) => (isMobile ? '100%' : '300px')};
  font-size: ${({ isMobile }) => (isMobile ? 14 : 16)}px;
  line-height: ${({ isMobile }) => (isMobile ? 16 : 20)}px;
  background-color: transparent;

  ${({ textColor }) =>
    textColor && `color: ${textColor}; border: 1px solid ${textColor}`}
`;

const StyledBanner = styled(HotelListBanner)`
  margin-bottom: 8px;
`;
